import { Transport } from './transport';
import {
    Message,
    EventMessage,
    WebRTCUpMessage,
    SlowlinkMessage,
    TimeoutMessage,
    HandleId,
    SessionId,
    HangupMessage,
    MediaMessage,
} from './message';

export type PluginMessage =
    | EventMessage
    | WebRTCUpMessage
    | MediaMessage
    | SlowlinkMessage
    | HangupMessage;

export interface PluginMessageReceivable {
    onReceive: (msg: PluginMessage) => void;
}

export class Session {
    static async create(transport: Transport) {
        const data = await transport.sendRequest<{ id: SessionId }>('create');
        const sessionId = data.id;
        return new Session(sessionId, transport);
    }

    readonly sessionId: number;
    readonly transport: Transport;

    private dispatchTable = new Map<number, PluginMessageReceivable>();
    private pendingMessages: PluginMessage[] = [];

    constructor(sessionId: number, transport: Transport) {
        this.sessionId = sessionId;
        this.transport = transport;

        setInterval(() => {
            this.transport.keepalive(this.sessionId);
        }, 60 * 0.8 * 1000);
    }

    startPolling() {
        console.log(`subscribeMessage: ${this.sessionId}`);
        this.transport.subscribeMessage(
            this.sessionId,
            async (msg: Message) => {
                if ('sender' in msg) {
                    const receiver = this.dispatchTable.get(msg.sender);
                    if (receiver) {
                        receiver.onReceive(msg);
                    } else {
                        console.warn(`unknown receiver '${msg.sender}'`, msg);
                        this.pendingMessages.push(msg);
                    }
                    return;
                }

                switch (msg.janus) {
                    case 'timeout':
                        this.ontimeout(msg);
                        break;
                    default:
                        console.log('unexpected session message', msg);
                        break;
                }
            }
        );
    }

    addReceiver(handleId: HandleId, receiver: PluginMessageReceivable) {
        const dispatchTable = this.dispatchTable;
        const pendingMessages = this.pendingMessages;

        dispatchTable.set(handleId, receiver);
        for (let i = pendingMessages.length - 1; i >= 0; --i) {
            const msg = pendingMessages[i];
            if (msg.sender === handleId) {
                receiver.onReceive(msg);
                pendingMessages.splice(i, 1);
            }
        }
    }

    async attach(opaqueId: string, plugin: string) {
        const data = await this.transport.sendRequest<{ id: HandleId }>(
            'attach',
            this.sessionId,
            {
                'opaque_id': opaqueId,
                plugin,
            }
        );
        return data.id;
    }

    async trickle(handleId: number, candidate: RTCIceCandidateInit) {
        return await this.sendCommand(handleId, 'trickle', {
            candidate,
        });
    }

    async sendMessage<T extends {}>(handleId: number, payload: T) {
        const msg = await this.sendCommand(handleId, 'message', payload);
        return msg;
    }

    private async sendCommand(handleId: number, command: string, body = {}) {
        return await this.transport.sendAsyncRequest(
            command,
            this.sessionId,
            handleId,
            body
        );
    }

    private ontimeout(msg: TimeoutMessage) {
        console.warn('session timeout', msg);
    }
}
